import { detectPushCapability, type PushCapability, type PushStatus } from '../domain/notification'

export type PushAttemptResult = { status: PushStatus; message: string }

function isIosRuntime(): boolean {
  return /iphone|ipad|ipod/iu.test(navigator.userAgent)
}

function isStandaloneRuntime(): boolean {
  const navigatorWithStandalone = navigator as Navigator & { standalone?: boolean }
  return navigatorWithStandalone.standalone === true || window.matchMedia('(display-mode: standalone)').matches
}

export function detectRuntimePushCapability(): PushCapability {
  return detectPushCapability({
    notificationPermission: typeof Notification === 'undefined' ? 'default' : Notification.permission,
    hasNotifications: typeof Notification !== 'undefined',
    hasServiceWorker: 'serviceWorker' in navigator,
    hasPushManager: 'PushManager' in window,
    isIos: isIosRuntime(),
    isStandalone: isStandaloneRuntime(),
  })
}

export function isIosBrowser(): boolean {
  return isIosRuntime()
}

function readPublicPushKey(): string {
  const value: unknown = import.meta.env.VITE_WEB_PUSH_PUBLIC_KEY
  return typeof value === 'string' ? value.trim() : ''
}

function decodeApplicationServerKey(value: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - value.length % 4) % 4)
  const decoded = atob((value + padding).replace(/-/gu, '+').replace(/_/gu, '/'))
  const bytes = new Uint8Array(decoded.length)
  for (let index = 0; index < decoded.length; index += 1) bytes[index] = decoded.charCodeAt(index)
  return bytes
}

export async function requestBestEffortPush(): Promise<PushAttemptResult> {
  const capability = detectRuntimePushCapability()
  if (capability === 'unavailable') {
    return { status: 'unavailable', message: 'Le Push n’est pas disponible ici. Les alertes restent dans Caravan.' }
  }
  if (capability === 'requires_install') {
    return { status: 'requires_install', message: 'Sur iPhone, ajoutez d’abord Caravan à l’écran d’accueil, puis réessayez depuis l’app installée.' }
  }
  if (capability === 'denied') {
    return { status: 'denied', message: 'Autorisation refusée. Les alertes restent dans Caravan.' }
  }
  const permission = capability === 'granted' ? 'granted' : await Notification.requestPermission()
  if (permission !== 'granted') {
    return { status: 'denied', message: 'Autorisation refusée. Les alertes restent dans Caravan.' }
  }

  const publicKey = readPublicPushKey()
  const { getSupabaseClient } = await import('../data/remote/supabaseClient')
  const client = getSupabaseClient()
  if (!publicKey || !client) {
    return { status: 'configuration_missing', message: 'Le canal Push distant n’est pas configuré. Les alertes internes restent actives.' }
  }

  try {
    const registration = await navigator.serviceWorker.ready
    const existing = await registration.pushManager.getSubscription()
    const subscription = existing ?? await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: decodeApplicationServerKey(publicKey),
    })
    const serialized = subscription.toJSON()
    const { error } = await client.rpc('caravan_upsert_push_subscription', {
      p_enabled: true,
      p_endpoint: subscription.endpoint,
      p_keys: serialized.keys ?? {},
    })
    if (error) throw new Error(error.message)
    return { status: 'granted', message: 'Push activé en best-effort. Les alertes internes restent la référence.' }
  } catch {
    return { status: 'unavailable', message: 'Activation Push impossible pour le moment. Aucune fonction Caravan n’est bloquée.' }
  }
}
